import type { Content, FloorDef, RewardType, RoomDef, RoomModifierDef, WaveDef } from '../data/schema';
import { grantRandomItem } from './equipment';
import type { MetaBonuses } from './meta/bonuses';
import { generateRoom } from './map/generate';
import { createRng, type Rng } from './rng';
import { collectPickup } from './systems/pickups';
import type { Pickup, Player, Vec2 } from './types';
import { spawn, type World } from './world';

/** `pos` : position de la porte sur une sortie de la salle courante (salle générée seulement). */
export interface DoorOffer { reward: RewardType; roomId: string; pos?: Vec2; }
/** Étage généré (Incarnam) : sous-zone et seed de chaque salle (5 de combat + boss), salles générées en cache. */
export interface GeneratedFloor { biomes: string[]; seeds: number[]; rooms: (RoomDef | undefined)[]; }
export interface FloorState { floor: FloorDef; order: string[]; roomIndex: number; history: RewardType[]; offered: RewardType[]; doors: DoorOffer[]; reward: RewardType | null; generated?: GeneratedFloor; }
export const ROOMS_TOTAL = 6;
const COMBAT_ROOMS = 5;
const ORDER: RewardType[] = ['kamas', 'xp', 'marchand', 'coffre', 'fontaine', 'defi'];
/** Monstre générique d'une vague d'étage généré : tiré dans la table d'apparition de la sous-zone. */
export const ANY_MONSTER = '*';

const genRoomId = (floor: FloorDef, k: number, biome: string): string => `${floor.id}-${k + 1}-${biome}`;

export function startFloor(content: Content, floorId: string, rng: Rng): FloorState {
  const floor = content.floors[floorId];
  if (!floor) throw new Error(`Étage introuvable : ${floorId}`);
  if (floor.generator) {
    // 5 sous-zones tirées dans le pool (jamais deux fois de suite la même), le boss dans sa sous-zone ; une seed par salle.
    const biomes: string[] = [];
    for (let k = 0; k < COMBAT_ROOMS; k++) {
      const pool = floor.roomPool.filter((b) => b !== biomes[k - 1]);
      biomes.push(rng.pick(pool.length ? pool : floor.roomPool));
    }
    biomes.push(floor.bossRoom);
    const seeds = biomes.map(() => rng.int(1, 0x7fffffff));
    const order = biomes.slice(0, COMBAT_ROOMS).map((b, k) => genRoomId(floor, k, b));
    return { floor, order, roomIndex: 0, history: [], offered: [], doors: [], reward: null, generated: { biomes, seeds, rooms: [] } };
  }
  const order = [...floor.roomPool];
  for (let i = order.length - 1; i > 0; i--) { const j = rng.int(0, i); [order[i], order[j]] = [order[j]!, order[i]!]; }
  return { floor, order, roomIndex: 0, history: [], offered: [], doors: [], reward: null };
}
export const isBossRoom = (s: FloorState): boolean => s.roomIndex >= COMBAT_ROOMS;
function bossRoomId(s: FloorState): string {
  return s.generated ? genRoomId(s.floor, COMBAT_ROOMS, s.generated.biomes[COMBAT_ROOMS]!) : s.floor.bossRoom;
}
export const currentRoomId = (s: FloorState): string => (isBossRoom(s) ? bossRoomId(s) : s.order[s.roomIndex % s.order.length]!);

/** Remplace les entrées « * » par des monstres tirés (pondérés) dans la table de la sous-zone. */
function resolveWaves(content: Content, biome: string, waves: WaveDef[], rng: Rng): WaveDef[] {
  const table = content.biomes[biome]?.spawns ?? [];
  const total = table.reduce((a, t) => a + t.weight, 0);
  const draw = (): string => {
    let r = rng.next() * total;
    for (const t of table) { r -= t.weight; if (r < 0) return t.id; }
    return table[table.length - 1]!.id;
  };
  return waves.map((w) => {
    const counts = new Map<string, number>();
    for (const g of w.monsters) {
      if (g.id !== ANY_MONSTER) { counts.set(g.id, (counts.get(g.id) ?? 0) + g.count); continue; }
      for (let k = 0; k < g.count; k++) { const id = draw(); counts.set(id, (counts.get(id) ?? 0) + 1); }
    }
    return { monsters: [...counts].map(([id, count]) => ({ id, count })) };
  });
}

/** Nombre maximal de modificateurs de salle appliqués à une run. */
export const MAX_ROOM_MODIFIERS = 2;

/** Modificateurs actifs : les deux premiers ids de `meta.roomModifiers` qui existent dans les données. */
export function activeModifiers(content: Content, meta: Pick<MetaBonuses, 'roomModifiers'>): RoomModifierDef[] {
  const out: RoomModifierDef[] = [];
  for (const id of meta.roomModifiers) {
    const m = content.modifiers[id];
    if (m && !out.includes(m)) out.push(m);
    if (out.length >= MAX_ROOM_MODIFIERS) break;
  }
  return out;
}

/** Augmente chaque groupe de monstres de `pct` % (arrondi au supérieur) ; les boss ne sont jamais dupliqués. */
function scaleCounts(content: Content, waves: WaveDef[], pct: number): WaveDef[] {
  if (pct === 0) return waves;
  return waves.map((w) => ({ monsters: w.monsters.map((g) => (content.monsters[g.id]?.boss ? g : { ...g, count: Math.ceil((g.count * (100 + pct)) / 100 - 1e-9) })) }));
}

export function roomSetup(s: FloorState, content: Content, mods: readonly RoomModifierDef[] = []): { room: RoomDef; waves: WaveDef[]; hpMult: number } {
  const countPct = mods.reduce((a, m) => a + m.monsterCountPct, 0);
  const bossHpMult = 1 + mods.reduce((a, m) => a + m.bossHpPct, 0) / 100;
  const k = Math.min(s.roomIndex, COMBAT_ROOMS);
  let room: RoomDef;
  let resolve = (w: WaveDef[]): WaveDef[] => w;
  if (s.generated) {
    const g = s.generated;
    const biome = g.biomes[k]!;
    room = g.rooms[k] ??= generateRoom(content, biome, g.seeds[k]!, { id: currentRoomId(s), name: content.biomes[biome]?.name });
    // tirage des monstres indépendant du combat : PRNG dérivé de la seed de la salle
    resolve = (w) => resolveWaves(content, biome, w, createRng((g.seeds[k]! ^ 0x9e3779b9) >>> 0));
  } else room = content.rooms[currentRoomId(s)]!;
  if (isBossRoom(s)) return { room, waves: scaleCounts(content, resolve(s.floor.bossWaves), countPct), hpMult: bossHpMult };
  const base = s.floor.encounters[k] ?? room.waves;
  const defi = s.reward === 'defi';
  const waves = defi && base.length ? [...base, structuredClone(base[base.length - 1]!)] : [...base];
  return { room, waves: scaleCounts(content, resolve(waves), countPct), hpMult: (1 + 0.18 * k) * (defi ? 1.25 : 1) };
}

/**
 * Actifs d'affichage de la salle d'indice `roomIndex` (courante ou à venir) : salle (générée et mise en cache si
 * besoin) et sprites des monstres de ses vagues. Sans effet sur l'état de l'étage ; le défi éventuel ne change pas
 * les monstres. Sert au client pour charger les atlas à l'entrée d'une salle et précharger la suivante.
 */
export function roomAssets(s: FloorState, content: Content, roomIndex: number): { room: RoomDef; sprites: string[] } {
  const setup = roomSetup({ ...s, roomIndex, reward: null, doors: [] }, content);
  const sprites = new Set<string>();
  for (const w of setup.waves) for (const g of w.monsters) { const m = content.monsters[g.id]; if (m) sprites.add(m.sprite); }
  return { room: setup.room, sprites: [...sprites] };
}

function drawWeighted(rng: Rng, entries: [RewardType, number][]): RewardType {
  const total = entries.reduce((a, [, w]) => a + w, 0);
  let r = rng.next() * total;
  for (const [t, w] of entries) { r -= w; if (r < 0) return t; }
  return entries[entries.length - 1]![0];
}

export function offerDoors(s: FloorState, rng: Rng): DoorOffer[] {
  const count = rng.int(2, 3);
  const toBoss = s.roomIndex === COMBAT_ROOMS - 1;
  const roomId = toBoss ? bossRoomId(s) : s.order[(s.roomIndex + 1) % s.order.length]!;
  let entries: [RewardType, number][] = ORDER.filter((t) => (s.floor.doorWeights[t] ?? 0) > 0).map((t) => [t, s.floor.doorWeights[t]!]);
  if (s.roomIndex < 1 || toBoss) entries = entries.filter(([t]) => t !== 'defi');
  if (toBoss) entries = entries.filter(([t]) => t !== 'marchand');
  const rewards: RewardType[] = [];
  if ((s.roomIndex === 2 || s.roomIndex === 3) && !s.history.includes('marchand') && !s.offered.includes('marchand')) { rewards.push('marchand'); entries = entries.filter(([t]) => t !== 'marchand'); }
  while (rewards.length < count && entries.length) { const t = drawWeighted(rng, entries); rewards.push(t); entries = entries.filter(([u]) => u !== t); }
  s.doors = rewards.map((reward) => ({ reward, roomId }));
  s.offered.push(...rewards);
  return s.doors;
}

export function chooseDoor(s: FloorState, index: number): DoorOffer | null {
  const d = s.doors[index];
  if (!d) return null;
  s.reward = d.reward; s.history.push(d.reward); s.roomIndex += 1; s.doors = [];
  return d;
}

function grant(world: World, p: Player, pickupType: 'kamas' | 'xp', amount: number): void {
  const pk = spawn<Pickup>(world, { kind: 'pickup', team: 'player', defId: pickupType, sprite: pickupType, pos: { ...p.pos }, vel: { x: 0, y: 0 }, radius: 8, facing: { x: 0, y: 0 }, hp: 1, hpMax: 1, alive: true, anim: 'idle', animTime: 0, pickupType, amount });
  collectPickup(world, p, pk);
}

export function applyEntryReward(world: World, p: Player, s: FloorState): void {
  if (s.reward !== 'fontaine') return;
  const before = p.hp;
  p.hp = Math.min(p.hpMax, p.hp + Math.floor(p.hpMax * 0.5));
  world.events.push({ type: 'heal', amount: p.hp - before, source: 'fontaine' });
}

export function applyClearReward(world: World, p: Player, s: FloorState): { openShop: boolean } {
  const n = s.roomIndex + 1;
  const mult = s.reward === 'defi' ? 2 : 1;
  if (s.reward === 'kamas' || s.reward === 'defi') grant(world, p, 'kamas', world.rng.int(25, 40) * n * mult);
  if (s.reward === 'xp' || s.reward === 'defi') grant(world, p, 'xp', 30 * n * mult);
  if (s.reward === 'coffre') grantRandomItem(world, p);
  return { openShop: s.reward === 'marchand' && !isBossRoom(s) };
}
